import { IThemeOptions, DEFAULT_THEME_OPTIONS, directionType } from "../models";
import { Observable, BehaviorSubject } from "rxjs";
import { DeviceService } from "./device.service";
import { getAssetPath } from "@stencil/core";
import ky from "ky";

const STYLESHEET_ID = "auvious-widget-styles";
class ThemeServiceCtrl {
  // behavior subject stores the last value even if no active subscription is available
  private optionsSubject: BehaviorSubject<IThemeOptions> = new BehaviorSubject(
    DEFAULT_THEME_OPTIONS
  );

  private options: IThemeOptions;
  private position = {
    horizontal: null,
    vertical: null,
  };

  setOptions(options: IThemeOptions, host?: HTMLElement) {
    if (!!host) {
      this.setDarkMode(options.darkMode, host);
    }
    try {
      const direction = getComputedStyle(document.body).getPropertyValue(
        "--av-direction"
      ) as directionType;
      options = { ...options, direction };
    } catch (ex) {
      // nada
    }
    this.options = options;
    this.optionsSubject.next(options);
  }

  setCustomAssets(options: { assetPath: string; assets: string[] }) {
    this.options = {
      ...this.options,
      assetPath: options.assetPath,
      assets: options.assets,
    };
    this.optionsSubject.next(this.options);
  }

  public async injectCSSVariables() {
    const style = document.createElement("style");
    style.id = STYLESHEET_ID;
    style.setAttribute("type", "text/css");
    // const rules = `:root { ${DEFAULT_CSS_VARIABLES} }`;
    const rules = await ky.get(getAssetPath(`auvious-assets/theme.css`)).text();
    const textNode = document.createTextNode(rules);
    // @ts-ignore
    !!style.styleSheet
      ? // @ts-ignore
        (style.styleSheet.cssText = rules)
      : style.appendChild(textNode);

    const nonce =
      window.__cspNonce ||
      document.querySelector('meta[name="csp-nonce"]')?.getAttribute("content");
    if (nonce) {
      style.setAttribute("nonce", nonce);
    }

    document.head.appendChild(style);
  }

  public calculateMobileViewportHeight() {
    // mobile safari fix
    // First we get the viewport height and we multiple it by 1% to get a value for a vh unit
    const vh = window.innerHeight * 0.01;
    // Then we set the value in the --av-vh custom property to the root of the document
    document.documentElement.style.setProperty("--av-vh", `${vh}px`);
  }

  // when we are in kiosk mode, we always want the chat to be on the left side, above the chat toggle button
  kioskCallStarted() {
    // backup position
    this.position.horizontal = this.options.positionHorizontal;
    this.position.vertical = this.options.positionVertical;
    // fix to bottom left
    this.optionsSubject.next({
      ...this.options,
      positionHorizontal: "left",
      positionVertical: "bottom",
    });
  }

  kioskCallEnded() {
    // restore backed up position
    this.setOptions({
      ...this.options,
      positionHorizontal:
        this.position.horizontal || this.options.positionHorizontal,
      positionVertical: this.position.vertical || this.options.positionVertical,
    });
  }

  optionsAvailable(): Observable<IThemeOptions> {
    return this.optionsSubject;
  }

  public async requestFullscreen(element: HTMLElement) {
    return (
      element.requestFullscreen?.() ||
      // @ts-expect-error
      element.webkitRequestFullscreen?.() ||
      // @ts-expect-error
      element.msRequestFullscreen?.() ||
      // @ts-expect-error
      element.mozRequestFullScreen?.()
    );
  }

  private setDarkMode(darkMode: boolean, host: HTMLElement) {
    const themeMode = darkMode ? "dark" : "light";
    host.setAttribute("data-av-theme", themeMode);
    host.setAttribute(
      "data-av-device",
      DeviceService.isMobile ? "mobile" : "desktop"
    );
    host.setAttribute(
      "data-av-device-os",
      DeviceService.isAndroid ? "android" : DeviceService.isIOS ? "ios" : "web"
    );
  }
}

export const ThemeService = new ThemeServiceCtrl();
